import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { base44 } from '@/api/base44Client';
import { X, Loader2, CheckCircle2, CalendarClock } from 'lucide-react';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';

export default function WaitlistJoinModal({ open, slot, date, rigType, experienceType, loggedInUser, onClose }) {
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setDone(false);
      setError('');
      setForm({
        customer_name: loggedInUser?.full_name || '',
        customer_email: loggedInUser?.email || '',
        customer_phone: loggedInUser?.phone || '',
        notes: '',
      });
    }
  }, [open, loggedInUser]);

  if (!open) return null;

  const dateStr = date ? format(date instanceof Date ? date : new Date(date), 'yyyy-MM-dd') : '';

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !slot || !dateStr || !rigType) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await base44.functions.invoke('joinWaitlist', {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        experience_type: experienceType || '',
        rig_type: rigType,
        date: dateStr,
        time_slot: slot,
        notes: form.notes,
      });
      if (!res?.data?.success) {
        throw new Error(res?.data?.error || 'Could not join the waitlist.');
      }
      queryClient.invalidateQueries({ queryKey: ['admin-waitlist'] });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not join the waitlist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const label = `${date ? format(date instanceof Date ? date : new Date(date), 'EEE dd MMM') : ''} · ${slot} · ${(rigType || '').replace(/_/g, ' ').toUpperCase()}`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-[#111115] border border-white/10 relative"
        >
          <button onClick={onClose} className="absolute top-3 right-3 text-white/30 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>

          {done ? (
            <div className="p-8 text-center">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}
                className="w-16 h-16 bg-primary flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </motion.div>
              <p className="text-primary text-[11px] font-bold tracking-widest mb-2">YOU'RE ON THE LIST</p>
              <h3 className="text-white font-black text-xl uppercase mb-2">Waitlist Joined</h3>
              <p className="text-white/40 text-sm leading-relaxed mb-6">
                If this slot frees up, we'll email <span className="text-white">{form.customer_email}</span> straight away so you can grab it before anyone else.
              </p>
              <button onClick={onClose} className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-3 transition-colors">
                DONE
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
                  <CalendarClock className="w-4.5 h-4.5 text-primary" />
                </div>
                <div>
                  <p className="text-primary text-[10px] font-bold tracking-widest">SOLD OUT</p>
                  <h3 className="text-white font-bold text-base leading-tight">Join the waitlist</h3>
                </div>
              </div>

              <p className="text-white/50 text-xs mb-5 leading-relaxed">
                This slot is fully booked. Add your details and we'll email you the moment it becomes available.
              </p>

              <div className="bg-[#0d0d0f] border border-white/5 px-3 py-2 mb-4">
                <p className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">SLOT</p>
                <p className="text-white/70 text-xs font-bold">{label}</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">NAME</label>
                  <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm h-11 px-3 placeholder:text-white/20 focus:border-primary/60 focus:outline-none" required />
                </div>
                <div>
                  <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">EMAIL</label>
                  <input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                    className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm h-11 px-3 placeholder:text-white/20 focus:border-primary/60 focus:outline-none" required />
                </div>
                <div>
                  <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">PHONE (OPTIONAL)</label>
                  <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                    className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm h-11 px-3 placeholder:text-white/20 focus:border-primary/60 focus:outline-none" />
                </div>
              </div>

              {error && <p className="text-red-400 text-xs mt-3">{error}</p>}

              <button type="submit" disabled={submitting}
                className="w-full mt-5 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-3.5 transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
                {submitting ? 'JOINING…' : 'JOIN WAITLIST'}
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}